import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import {
  downloadImportTemplate,
  getImportConfig,
  importMasterFile,
} from "../../../services/api.service";

/**
 * Smart Import - one button, reused by every master list screen.
 *
 * Flow
 *   1. "Import" opens a modal with exactly two choices:
 *        - Download Template : an .xlsx built from the server side column
 *          definition, with dropdowns pre-filled from the related masters.
 *        - Upload Template   : the SAME file, filled in.
 *   2. The server fingerprints the template it hands out, so any other
 *      workbook (a hand made sheet, another master's template, an edited
 *      header row) is rejected before a single row is written.
 *   3. The result step reports how many rows went in and lists the rows that
 *      did not, with the reason.
 *
 * Usage:
 *   <SmartImportButton module="city" label="City" onImported={refetch} />
 */
const SmartImportButton = ({
  module: moduleKey,
  label = "",
  onImported,
  color = "success",
  className = "waves-effect waves-light me-1",
  style,
}) => {
  const fileInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [busyText, setBusyText] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const title = label || config?.label || "Records";

  /* column list is only needed once the modal is opened */
  useEffect(() => {
    if (!isOpen || config) return;

    let cancelled = false;
    getImportConfig(moduleKey)
      .then((response) => {
        if (cancelled) return;
        if (response?.code === 1) setConfig(response.data);
        else setError(response?.message || "Import is not configured for this master.");
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load the import configuration.");
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, config, moduleKey]);

  const resetTransient = () => {
    setError("");
    setResult(null);
  };

  const open = () => {
    resetTransient();
    setIsOpen(true);
  };

  const close = () => {
    if (isBusy) return;
    setIsOpen(false);
    resetTransient();
  };

  const handleDownload = useCallback(async () => {
    resetTransient();
    setIsBusy(true);
    setBusyText("Preparing template...");
    try {
      await downloadImportTemplate(
        moduleKey,
        `${title.replace(/\s+/g, "_")}_Import_Template.xlsx`
      );
    } catch (downloadError) {
      setError(downloadError.message || "Unable to download the template.");
    } finally {
      setIsBusy(false);
      setBusyText("");
    }
  }, [moduleKey, title]);

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file after a fix
    if (!file) return;

    resetTransient();

    // first gate is here so an obviously wrong file never leaves the browser
    if (!/\.xlsx$/i.test(file.name)) {
      setError(
        "Only the downloaded template (.xlsx) can be uploaded. " +
          "Older .xls and .csv files are not accepted."
      );
      return;
    }

    setIsBusy(true);
    setBusyText(`Importing "${file.name}"...`);

    try {
      const response = await importMasterFile(moduleKey, file);

      if (response?.code === 1) {
        setResult(response.data);
        if (response.data?.inserted > 0 && typeof onImported === "function") {
          onImported();
        }
      } else {
        setError(response?.message || "Import failed.");
      }
    } catch (uploadError) {
      setError(uploadError.message || "Import failed.");
    } finally {
      setIsBusy(false);
      setBusyText("");
    }
  };

  const optionCardStyle = {
    cursor: isBusy ? "not-allowed" : "pointer",
    opacity: isBusy ? 0.6 : 1,
    transition: "box-shadow .15s ease, border-color .15s ease",
  };

  return (
    <>
      <Button
        color={color}
        className={className}
        onClick={open}
        style={style}
        title={`Import ${title} from an Excel template`}
      >
        <i className="mdi mdi-file-import-outline me-1" />
        Import
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleFileSelected}
      />

      <Modal isOpen={isOpen} toggle={close} centered size="lg" backdrop="static">
        <ModalHeader toggle={isBusy ? undefined : close}>
          <i className="mdi mdi-flash-outline me-1 text-primary" />
          Smart Import — {title}
        </ModalHeader>

        <ModalBody>
          {error && (
            <Alert color="danger" className="mb-3">
              <i className="mdi mdi-alert-circle-outline me-1" />
              {error}
            </Alert>
          )}

          {isBusy && (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="text-muted mt-2 mb-0">{busyText}</p>
            </div>
          )}

          {!isBusy && result && (
            <ImportResult result={result} title={title} />
          )}

          {!isBusy && !result && (
            <>
              <p className="text-muted">
                Download the template, fill your {title.toLowerCase()} data in it and
                upload the same file. Only this template format is accepted.
              </p>

              <div className="row g-3">
                <div className="col-md-6">
                  <div
                    className="border rounded p-3 h-100"
                    style={optionCardStyle}
                    role="button"
                    tabIndex={0}
                    onClick={handleDownload}
                    onKeyDown={(event) =>
                      event.key === "Enter" ? handleDownload() : null
                    }
                  >
                    <h6 className="mb-1">
                      <i className="mdi mdi-tray-arrow-down text-primary me-1" />
                      Download Template
                    </h6>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                      Excel file with the exact columns, dropdowns filled from the
                      related masters and an Instructions sheet.
                    </p>
                  </div>
                </div>

                <div className="col-md-6">
                  <div
                    className="border rounded p-3 h-100"
                    style={optionCardStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(event) =>
                      event.key === "Enter" ? fileInputRef.current?.click() : null
                    }
                  >
                    <h6 className="mb-1">
                      <i className="mdi mdi-tray-arrow-up text-success me-1" />
                      Upload Template
                    </h6>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                      Pick the filled <strong>.xlsx</strong> template. Anything else,
                      or an edited header row, is rejected.
                    </p>
                  </div>
                </div>
              </div>

              {config?.columns?.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-2">
                    Expected columns
                    <small className="text-muted ms-2">
                      (highlighted ones are mandatory)
                    </small>
                  </h6>
                  <div className="d-flex flex-wrap gap-1">
                    {config.columns.map((column) => (
                      <Badge
                        key={column.header}
                        color={column.required ? "primary" : "light"}
                        className={column.required ? "" : "text-dark border"}
                        pill
                      >
                        {column.header}
                      </Badge>
                    ))}
                  </div>
                  {config.maxRows && (
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.8rem" }}>
                      Up to {config.maxRows} rows per file.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {result && (
            <Button
              color="secondary"
              outline
              disabled={isBusy}
              onClick={() => {
                resetTransient();
                fileInputRef.current?.click();
              }}
            >
              Import Another File
            </Button>
          )}
          <Button color="primary" onClick={close} disabled={isBusy}>
            {result ? "Done" : "Close"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

/** Post-import summary: what went in, and why the rest did not. */
const ImportResult = ({ result, title }) => {
  const { totalRows = 0, inserted = 0, failed = 0, errors = [] } = result || {};

  return (
    <>
      <div className="d-flex flex-wrap gap-3 mb-3">
        <SummaryTile label="Rows in file" value={totalRows} color="secondary" />
        <SummaryTile label="Imported" value={inserted} color="success" />
        <SummaryTile label="Skipped" value={failed} color={failed ? "danger" : "secondary"} />
      </div>

      {failed === 0 ? (
        <Alert color="success" className="mb-0">
          <i className="mdi mdi-check-circle-outline me-1" />
          All {inserted} {title.toLowerCase()} record(s) were imported successfully.
        </Alert>
      ) : (
        <>
          <Alert color="warning">
            <i className="mdi mdi-alert-outline me-1" />
            {failed} row(s) were skipped. Fix them in the template and upload again —
            the {inserted} row(s) that already went in will be reported as duplicates
            and skipped.
          </Alert>

          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            <Table size="sm" bordered className="mb-0">
              <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ width: 80 }}>Row</th>
                  <th style={{ width: 200 }}>Value</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((item) => (
                  <tr key={`${item.row}-${item.message}`}>
                    <td>{item.row}</td>
                    <td className="text-truncate" style={{ maxWidth: 200 }}>
                      {item.values || "-"}
                    </td>
                    <td>{item.message}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {result.truncatedErrors && (
            <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.8rem" }}>
              Only the first {errors.length} errors are listed.
            </p>
          )}
        </>
      )}
    </>
  );
};

const SummaryTile = ({ label, value, color }) => (
  <div className={`border border-${color} rounded px-3 py-2 text-center`}>
    <div className={`h4 mb-0 text-${color}`}>{value}</div>
    <small className="text-muted">{label}</small>
  </div>
);

export default SmartImportButton;
